//#region Imports
// Node Packages
import React, { useContext, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { PrimaryButton } from 'swp-react';
// Redux Slices
import {
	selectHostName,
	selectLocalDomain,
	selectUserId,
} from '@/slices/commonSlice';
import {
	selectCurrentUploadDetails,
	selectTotalImagesFailed,
	selectTotalImagesUploaded,
	setCurrentUploadDetails,
	setFailedImages,
	setTotalImagesFailed,
	setTotalImagesUploaded,
	setTotalImageUploadCount,
	setUnsupportedFiles,
	setUploadCount,
	setUploadInProgress,
} from '@/slices/uploadSlice';
// JavaScript Util Helper Functions
import { getGuid, updateUploadDetails, uploadImage } from '@/utils/uploadUtils';
import { logToAi, fileArrayStats } from '@/utils/logging';
import { addToQueue } from '@/utils/requestQueue';
import netreq from '@/utils/netreq';
import { fetchCall } from '@/utils/fetchUtils';
// React Components and Wrappers
import FormattedMessage from '../FormattedMessage';
import { ConfigContext } from '../UserAgentContext';
// CSS Styling
import AddPicturesStyle from './AddPictures.module.scss';
//#endregion

const AddPicturesButton = (props) => {
	//#region Hooks and State
	const inputtarget = useRef();
	const dispatch = useDispatch();
	const config = useContext(ConfigContext);

	const [isLoading, setIsLoading] = useState(false);

	const docMode = props.docMode;
	const propTitleId = props?.titleId;
	const enableFaceDetection = config?.enableFaceDetection;
	const faceDetectionThreshold = config?.faceDetectionThreshold;
	//#endregion
	//#region Redux Selectors
	const totalImagesUploaded = useSelector(selectTotalImagesUploaded);
	const totalFailedUploads = useSelector(selectTotalImagesFailed);
	const localDomain = useSelector(selectLocalDomain);
	const currentUploadDetails = useSelector(selectCurrentUploadDetails);
	const host = useSelector(selectHostName);

	const uid = useSelector(selectUserId);
	//#endregion
	//#region SUPPORTED_FILE_EXTENSIONS
	const SUPPORTED_FILE_EXTENSIONS = useMemo(() => {
		if (docMode && host) {
			return [
				'.heic',
				'.heif',
				'.jpeg',
				'.jpg',
				'.png',
				'.bmp',
				'.pdf',
				'.doc',
				'.docx',
				'.ppt',
				'.pptx',
				'.odt',
				'.odp',
				'.fodp',
				'.fodt',
			];
		}
		if (docMode) {
			return ['.heic', '.heif', '.jpeg', '.jpg', '.png', '.bmp', '.pdf'];
		}
		return ['.heic', '.heif', '.jpeg', '.jpg', '.png', '.bmp'];
	}, [docMode]);
	//#endregion
	//#region allowedFileTypes
	const allowedFileTypes = useMemo(() => {
		if (docMode && host) {
			return 'image/jpeg,image/png,image/heic,image/heif,image/bmp,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.ms-powerpoint,application/vnd.openxmlformats-officedocument.presentationml.presentation,application/vnd.oasis.opendocument.text,application/vnd.oasis.opendocument.presentation';
		}
		if (docMode) {
			return 'image/jpeg,image/png,image/heic,image/heif,image/bmp,application/pdf';
		}
		return config.acceptedFileTypes;
	}, [docMode, config.acceptedFileTypes, host]);
	//#endregion
	//#region isWebKit
	const isWebKit = useMemo(() => {
		const ua = navigator?.userAgent;

		// Safari, iOS Safari, and other browsers using WebKit
		return /iPhone|iPad|iPod/i.test(ua);
	}, [navigator?.userAgent]);
	//#endregion
	//#region buttonId
	const buttonId = propTitleId
		? propTitleId
		: docMode
			? 'add-documents-button'
			: 'add-pictures-button';
	//#endregion
	//#region triggerImageSelect
	const triggerImageSelect = () => {
		logToAi(
			{
				message: `AddPicturesButton pressed!`,
				id: 'ADD_PICTURES_BUTTON_PRESS',
				location: 'AddPicturesButton.jsx:triggerImageSelect',
			},
			'debug'
		);

		inputtarget.current.click();
		setIsLoading(true);
		const inputSelected = document.getElementById('addfiles');
		inputSelected.addEventListener('cancel', () => {
			setIsLoading(false);
		});
	};
	//#endregion
	//#region createStatusJpegFile
	const createStatusJpegFile = (filename) => {
		return new Promise((resolve) => {
			const canvas = document.createElement('canvas');
			canvas.width = 1;
			canvas.height = 1;
			const context = canvas.getContext('2d');
			if (context) {
				context.fillStyle = '#ffffff';
				context.fillRect(0, 0, 1, 1);
			}
			canvas.toBlob(
				(blob) => {
					const jpegBlob =
						blob ||
						new Blob([new Uint8Array([0xff, 0xd8, 0xff, 0xd9])], {
							type: 'image/jpeg',
						});
					resolve(
						new File([jpegBlob], filename, {
							type: 'image/jpeg',
						})
					);
				},
				'image/jpeg',
				1
			);
		});
	};
	//#endregion
	//#region submitFiles
	const submitFiles = async (files) => {
		return new Promise(async (resolve, reject) => {
			const blobUploadSessionId =
				crypto?.randomUUID?.() ||
				getGuid() ||
				'Could not generate an upload session id';
			dispatch(setTotalImageUploadCount(files.length));
			dispatch(setTotalImagesUploaded(files.length));
			dispatch(setUploadInProgress(true));
			const timerStart = performance.now();
			const queue = [...files];
			let resultsArray = [];
			let errored = [];
			const userId = uid;

			const convertToBlob = async (file) => {
				if (isWebKit) {
					console.debug('browser is WebKit, bypassing convertToBlob');
					// VGR-6835 skipping arrayBuffer conversion for WebKit browsers to avoid memory issues with file uploads
					return file;
				}
				try {
					const buffer = await file.arrayBuffer();
					return new File([buffer], file.name, {
						type: file.type,
						lastModified: file.lastModified,
					});
				} catch (error) {
					logToAi(
						{
							message: `Error while converting image to blob '${file?.name}'`,
							id: 'CONVERT_TO_BLOB_ERROR',
							location: 'uploadUtils.js:uploadImage',
							parameters: {
								file: {
									name: file?.name,
									size: file?.size,
									type: file?.type,
								},
								userId: userId,
							},
							data: {
								error: error,
							},
						},
						'error'
					);
					throw error;
				}
			};

			const onUpload = (result, errorResult = false) => {
				resultsArray = [...resultsArray, result];
				if (
					result instanceof Error ||
					result?.error ||
					result?.statusCode >= 400 ||
					errorResult
				) {
					errored = [...errored, result];
				}

				if (queue.length) {
					const nextFile = queue.shift();

					return convertToBlob(nextFile)
						.then((blob) =>
							dispatch(
								uploadImage({
									file: blob,
									userId: userId,
									localDomain: localDomain,
									enableFaceDetection: enableFaceDetection,
									faceDetectionThreshold:
										faceDetectionThreshold,
								})
							)
								.then(onUpload)
								.catch((err) =>
									onUpload(
										{
											...err,
											fileName: blob?.name,
											file: blob,
										},
										true
									)
								)
						)
						.catch((err) =>
							onUpload(
								{
									...err,
									fileName: nextFile?.name,
									file: nextFile,
								},
								true
							)
						);
				}

				return result;
			};

			if (localDomain) {
				fetchCall(
					`${localDomain}/spelite/metadata`,
					'POST',
					{ count: files.length },
					{ 'content-type': 'application/json' }
				);
			}

			const filesStats = fileArrayStats(files);

			logToAi(
				{
					message: `Started file upload!`,
					id: 'UPLOAD_BEGIN',
					location: 'AddPicturesButton.jsx:submitFiles',
					data: {
						userId: userId,
						...filesStats,
					},
				},
				'debug'
			);

			let promises = queue.splice(0, 5).map((file) =>
				convertToBlob(file)
					.then((blob) =>
						dispatch(
							uploadImage({
								file: blob,
								userId: userId,
								localDomain: localDomain,
								enableFaceDetection: enableFaceDetection,
								faceDetectionThreshold: faceDetectionThreshold,
							})
						)
							.then(onUpload)
							.catch((err) =>
								onUpload(
									{
										...err,
										fileName: blob?.name,
										file: blob,
									},
									true
								)
							)
					)
					.catch((err) =>
						onUpload(
							{ ...err, fileName: file?.name, file: file },
							true
						)
					)
			);

			try {
				const uploadObj = {
					totalCount: currentUploadDetails?.totalCount + files.length,
					failedCount: currentUploadDetails?.failedCount,
				};
				const updateDetails = await dispatch(
					updateUploadDetails(userId, uploadObj)
				);
				dispatch(setCurrentUploadDetails(uploadObj));
			} catch (err) {}
			Promise.allSettled(promises)
				.then((resources) => {
					logToAi(
						{
							message: `Upload of ${
								resultsArray.length - errored.length
							} files complete${
								errored.length > 0
									? `, with ${errored.length} files returning an error!`
									: '!'
							}`,
							id: `UPLOAD_COMPLETE${
								errored.length > 0 ? '_WITH_ERROR' : ''
							}`,
							location: 'AddPicturesButton.jsx:submitFiles',
							data: {
								filesErroring: errored.length,
								userId: userId,
								...filesStats,
							},
						},
						`${errored.length > 0 ? 'error' : 'debug'}`
					);
				})
				.catch((err) => {
					logToAi(
						{
							message: `Error while uploading files!`,
							id: 'UPLOAD_IMAGE_ERROR',
							location: 'AddPicturesButton.jsx:submitFiles',
							data: {
								error: err,
								userId: userId,
								...filesStats,
							},
						},
						'error'
					);
					reject(err);
				})
				.then(async () => {
					const timerEnd = performance.now();
					dispatch(setTotalImageUploadCount(0));
					dispatch(setUploadCount(0));
					if (errored.length > 0) {
						dispatch(setTotalImagesFailed(errored.length));
						dispatch(
							setFailedImages(
								errored.map((e) => e?.file || 'undefined')
							)
						);
					}
					setIsLoading(false);
					resolve();
					const uploadTime = (timerEnd - timerStart) / 1000;
					const uploadTimePerImage =
						uploadTime / resultsArray?.length;
					const uploadTimePerSuccessfulImage =
						uploadTime / (resultsArray?.length - errored?.length);
					const mbps =
						(
							parseInt(filesStats?.totalFilesSize.split(' ')[0]) /
							uploadTime
						).toFixed(2) + ' MB/s';
					console.log(`Total Upload time: ${uploadTime}`);
					logToAi(
						{
							message: `Upload of ${
								resultsArray.length - errored.length
							} files complete${
								errored.length > 0
									? `, with ${errored.length} files returning an error!`
									: '!'
							} Total Upload time: ${uploadTime} at a speed of ${mbps}`,
							id: 'UPLOAD_TIME',
							location: 'AddPicturesButton.jsx:submitFiles',
							data: {
								totalFiles: resultsArray?.length,
								filesErroring: errored.length,
								filesSucceeding:
									resultsArray?.length - errored?.length,

								uploadTime: uploadTime,
								uploadTimePerImage,
								uploadTimePerSuccessfulImage,
								uploadSpeed: mbps,

								userId: userId,
								...filesStats,
							},
						},
						'debug'
					);
				});
		});
	};
	//#endregion
	//#region handleOnChange
	const handleOnChange = (event) => {
		if (event?.target?.files) {
			let imageArray = Array.from(event.target.files);
			const invalidFiles = [];
			imageArray = imageArray.filter((image) => {
				if (image?.size < 1) {
					invalidFiles.push(image);
					return false;
				}
				if (
					image.type !== '' &&
					allowedFileTypes.includes(image.type)
				) {
					return true;
				}
				if (
					image.type === '' &&
					SUPPORTED_FILE_EXTENSIONS.some((t) =>
						image?.name?.toLowerCase()?.endsWith(t)
					)
				) {
					return true;
				}
				invalidFiles.push(image);
				return false;
			});

			if (invalidFiles.length > 0) {
				logToAi(
					{
						message: 'unsupported images detected',
						id: 'UNSUPPORTED_FILES',
						location: 'AddPicturesButton.jsx:handleOnChange',
						data: {
							invalidFiles: invalidFiles,
							totalFiles: event?.target?.files?.length,
						},
					},
					'error'
				);
				dispatch(setUnsupportedFiles(invalidFiles));
			}

			if (imageArray.length > 0) {
				submitFiles(imageArray)
					.then(() => {
						event.target.value = '';
						setIsLoading(false);
					})
					.catch((err) => {
						event.target.value = '';
						setIsLoading(false);
					});
				return;
			} else {
				event.target.value = '';
				setIsLoading(false);
			}

			// log({
			//     description: `Image upload not started; no valid images were selected!`,
			//     location: ["AddPicturesButton.js", "input-onChange"],
			// });
			return;
		} else {
			setIsLoading(false);
		}
	};
	//#endregion
	//#region Render UI
	return (
		<div className={AddPicturesStyle['add-pictures-button-container']}>
			<PrimaryButton
				width={'100%'}
				height={'56px'}
				className="local-upload"
				onClick={() => {
					triggerImageSelect();
				}}
				loading={isLoading}
				onKeyPress={(event) => {
					event.preventDefault();
					if (event.keyCode === 0) {
						triggerImageSelect();
					}
				}}
			>
				<FormattedMessage id={buttonId} />
			</PrimaryButton>
			<div className="hidden">
				<input
					accept={`${allowedFileTypes},image/avif`}
					className="hidden"
					type="file"
					id="addfiles"
					ref={inputtarget}
					name="addfiles"
					multiple={true}
					aria-hidden="true"
					aria-label="Add Images"
					onChange={handleOnChange}
				/>
			</div>
		</div>
	);
	//#endregion
};

AddPicturesButton.displayName = 'AddPicturesButton';
export default AddPicturesButton;
